// src/components/product/ProductCard.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function ProductCard({ product, onAddToCart }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [added, setAdded]   = useState(false);

  if (!product) return null;

  const slug   = product.slug || product.id;
  const nameFr = product.nom || product.name || '';
  const nameEn = product.nom_en || product.name_en;
  const nameAr = product.nom_ar || product.name_ar;
  const name =
    i18n.language === 'ar' && nameAr ? nameAr :
    i18n.language === 'en' && nameEn ? nameEn :
    nameFr;

  const originalPrice = Number(product.prix ?? product.price ?? 0);
  const discount      = Number(product.discount ?? 0);
  const newPrice      = product.new_price != null ? Number(product.new_price) : null;
  const hasPromo      = discount > 0 && newPrice != null;

  const imageRaw = product.image || product.image_url || '';
  const image = imageRaw.startsWith('http')
    ? imageRaw
    : `https://madadline.com/api/images/${imageRaw.split('/').pop()}`;

  const stock = (product.variants && product.variants.length > 0)
    ? product.variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0)
    : Number(product.stock ?? 0);

  // ⭐ Flag "sur commande uniquement"
  const isRequestOnly = Boolean(product.is_request_only);

  let status = {
    type:  'in-stock',
    dot:   '#22C55E',
    label: t('productCard.inStock'),
    badge: null,
  };

  if (stock === 0) {
    status = {
      type: 'out',
      dot:  '#999999',
      label: t('productCard.outOfStock'),
      badge: { type: 'out', label: t('productCard.outOfStock') },
    };
  } else if (stock <= 3) {
    status = {
      type: 'low',
      dot:  '#F59E0B',
      label: t('productCard.lowStock', { n: stock }),
      badge: { type: 'low', label: t('productCard.lowBadge') },
    };
  } else if (product.is_new || product.nouveau) {
    status = {
      type: 'new',
      dot:  '#22C55E',
      label: t('productCard.new'),
      badge: { type: 'new', label: t('productCard.new') },
    };
  }

  const colors = product.couleurs || product.colors || [];
  const categories = product.categories || [];

  /* ⭐ "Ajouter" → navigue vers la page produit pour choisir taille/couleur */
  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (stock === 0 || isRequestOnly) return;
    navigate(`/product/${slug}`);
  };

  const handleRequest = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isRequestOnly) return;

    const oneShotItem = {
      id: `oneshot-${Date.now()}`,
      product,
      quantity: 1,
      size: null,
      color: null,
      material: null,
    };

    window.dispatchEvent(new CustomEvent('open-checkout-request', {
      detail: { items: [oneShotItem] },
    }));
  };

  return (
    <article className={`pc pc--${status.type} ${hasPromo && !isRequestOnly ? 'pc--promo' : ''} ${isRequestOnly ? 'pc--request-only' : ''}`}>
      <Link to={`/product/${slug}`} className="pc__link">

        <div className="pc__media">
          {image && <img src={image} alt={name} loading="lazy" />}

          {hasPromo && !isRequestOnly && (
            <span className="pc__badge pc__badge--promo">-{discount}%</span>
          )}

          {isRequestOnly && (
            <span className="pc__badge pc__badge--request">
              {t('productCard.requestOnlyBadge')}
            </span>
          )}

          {!hasPromo && !isRequestOnly && status.badge && (
            <span className={`pc__badge pc__badge--${status.badge.type}`}>
              {status.badge.label}
            </span>
          )}

          <span className="pc__stock-dot" style={{ background: status.dot }} />
        </div>

        <div className="pc__info">
          {categories.length > 0 && (
            <div className="pc__categories" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              marginBottom: 6,
            }}>
              {categories.slice(0, 3).map(cat => (
                <span
                  key={cat.id}
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: '#888',
                    padding: '2px 6px',
                    background: '#F8F6F0',
                    borderRadius: 4,
                  }}
                >
                  {cat.name}
                </span>
              ))}
              {categories.length > 3 && (
                <span style={{ fontSize: 9, color: '#888', padding: '2px 4px' }}>
                  +{categories.length - 3}
                </span>
              )}
            </div>
          )}

          <h3 className="pc__name">{name}</h3>

          {isRequestOnly ? (
            <div className="pc__price-row pc__price-row--request">
              <span className="pc__price pc__price--request">
                {t('productCard.priceOnRequest')}
              </span>
            </div>
          ) : hasPromo ? (
            <div className="pc__price-row pc__price-row--promo">
              <span className="pc__price pc__price--promo">
                {newPrice.toFixed(0)} DH
              </span>
              <span className="pc__price--old">
                {originalPrice.toFixed(0)} DH
              </span>
            </div>
          ) : (
            <div className="pc__price-row">
              <span className="pc__price">{originalPrice.toFixed(0)} DH</span>
            </div>
          )}

          {isRequestOnly ? (
            <span className="pc__status pc__status--request">
              {t('productCard.onRequestStatus')}
            </span>
          ) : (
            <span className={`pc__status pc__status--${status.type}`}>
              <span className="pc__status-dot" style={{ background: status.dot }} />
              {status.label}
            </span>
          )}

          {colors.length > 0 && (
            <div className="pc__colors">
              {colors.slice(0, 4).map((c, i) => (
                <span
                  key={i}
                  style={{ background: typeof c === 'string' ? c : c.hex || c.code }}
                  title={typeof c === 'object' ? c.name : ''}
                />
              ))}
              {colors.length > 4 && <em>+{colors.length - 4}</em>}
            </div>
          )}
        </div>
      </Link>

      {!isRequestOnly ? (
        <button
          className={`pc__add ${added ? 'is-added' : ''} ${adding ? 'is-loading' : ''}`}
          onClick={handleAdd}
          disabled={adding || stock === 0}
          type="button"
        >
          <span className="pc__add-bg" />
          <span className="pc__add-content">
            {stock === 0 ? (
              <>
                <svg className="pc__add-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                <span>{t('productCard.outOfStock')}</span>
              </>
            ) : (
              <>
                <svg className="pc__add-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                <span>{t('productCard.add')}</span>
              </>
            )}
          </span>
        </button>
      ) : (
        <button
          className="pc__add pc__add--request"
          onClick={handleRequest}
          type="button"
        >
          <span className="pc__add-bg" />
          <span className="pc__add-content">
            <svg className="pc__add-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            <span>{t('productCard.orderRequest')}</span>
          </span>
        </button>
      )}
    </article>
  );
}

export default ProductCard;